import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";

/**
 * Payment confirmation page — shows BOC bank transfer details.
 * Reached via <Link to="/payment?sp={sponsorship_id}"> after a sponsorship is created.
 */
export default function Payment() {
    const { search } = useLocation();
    const nav = useNavigate();
    const spId = new URLSearchParams(search).get("sp");
    const [sp, setSp] = useState(null);
    const [bank, setBank] = useState(null);
    const [copied, setCopied] = useState("");

    useEffect(() => {
        api.get("/payment/bank").then((r) => setBank(r.data)).catch(() => {});
        if (spId) {
            api.get(`/sponsorships/${spId}`)
                .then((r) => setSp(r.data))
                .catch(() => {});
        }
    }, [spId]);

    const reference = sp
        ? `${bank?.reference_prefix || "SPONSO"}-${(sp.id || "").slice(0, 8).toUpperCase()}`
        : null;

    const copy = async (key, value) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(key);
            toast.success("Copié dans le presse-papier");
            setTimeout(() => setCopied(""), 2000);
        } catch {
            toast.error("Copie impossible");
        }
    };

    return (
        <div className="bg-[#09090b] text-zinc-100 min-h-[70vh]">
            <section className="border-b border-zinc-900 px-6 lg:px-8 pt-16 pb-10">
                <div className="max-w-4xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                        Paiement par virement
                    </div>
                    <h1 className="font-display text-5xl uppercase leading-[0.9] tracking-tighter">
                        Finalisez votre <span className="text-orange-500">sponsoring</span>
                    </h1>
                    <p className="mt-4 text-zinc-300 max-w-2xl">
                        Effectuez un virement bancaire sur le compte BOC ci-dessous.
                        Une fois le virement reçu, votre sponsoring sera automatiquement validé.
                    </p>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 lg:px-8 py-10 space-y-6">
                {sp && (
                    <div className="card-tu" data-testid="payment-summary">
                        <h2 className="font-display text-2xl uppercase tracking-tight mb-4">Votre commande</h2>
                        <dl className="grid grid-cols-2 gap-y-3 text-sm">
                            <dt className="text-zinc-500 uppercase tracking-widest text-[11px]">Club</dt>
                            <dd className="font-bold">{sp.club_name}</dd>
                            <dt className="text-zinc-500 uppercase tracking-widest text-[11px]">Catégorie</dt>
                            <dd>{sp.age_category}</dd>
                            <dt className="text-zinc-500 uppercase tracking-widest text-[11px]">Maillots</dt>
                            <dd>{sp.num_players}</dd>
                            <dt className="text-zinc-500 uppercase tracking-widest text-[11px]">Total TTC</dt>
                            <dd className="font-display text-3xl text-orange-500">{sp.total_amount}€</dd>
                        </dl>
                    </div>
                )}

                {bank ? (
                    <div className="card-tu" data-testid="payment-bank-details">
                        <h2 className="font-display text-2xl uppercase tracking-tight mb-4">
                            Coordonnées bancaires BOC
                        </h2>
                        <div className="space-y-3">
                            <BankRow label="Banque" value={bank.bank_name} testid="bank-name" />
                            <BankRow label="Bénéficiaire" value={bank.beneficiary} testid="bank-beneficiary" />
                            <BankRow
                                label="IBAN"
                                value={bank.iban}
                                onCopy={() => copy("iban", bank.iban)}
                                copied={copied === "iban"}
                                mono
                                testid="bank-iban"
                            />
                            <BankRow
                                label="BIC / SWIFT"
                                value={bank.bic}
                                onCopy={() => copy("bic", bank.bic)}
                                copied={copied === "bic"}
                                mono
                                testid="bank-bic"
                            />
                            {reference && (
                                <BankRow
                                    label="Référence (obligatoire)"
                                    value={reference}
                                    onCopy={() => copy("ref", reference)}
                                    copied={copied === "ref"}
                                    mono
                                    testid="bank-reference"
                                />
                            )}
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-5 leading-relaxed">
                            ⚠️ <span className="font-bold text-orange-500">Indiquez la référence</span> dans le libellé du virement
                            pour que notre équipe identifie votre sponsoring. Un email de confirmation sera envoyé après réception
                            (1 à 3 jours ouvrés selon votre banque).
                        </p>
                    </div>
                ) : (
                    <div className="text-zinc-500 text-center py-10">Chargement des coordonnées bancaires…</div>
                )}

                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => nav("/dashboard/sponsor")}
                        data-testid="payment-done"
                        className="btn-primary"
                    >
                        J'ai effectué le virement — Retour à mon espace
                    </button>
                    <Link to="/clubs" className="btn-ghost">
                        Sponsoriser un autre club
                    </Link>
                </div>
            </section>
        </div>
    );
}

function BankRow({ label, value, mono, onCopy, copied, testid }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 pb-3 last:border-b-0 last:pb-0">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
                <div
                    data-testid={testid}
                    className={mono ? "font-mono text-sm md:text-base text-zinc-200" : "text-sm md:text-base text-zinc-200"}
                >
                    {value || "—"}
                </div>
            </div>
            {onCopy && value && (
                <button
                    onClick={onCopy}
                    className="text-xs uppercase tracking-widest text-orange-500 hover:underline inline-flex items-center gap-1"
                    data-testid={`${testid}-copy`}
                >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Copié" : "Copier"}
                </button>
            )}
        </div>
    );
}
